import { useCallback, useEffect, useMemo, useState } from 'react'
import { Award } from 'lucide-react'
import DateRangeField from '../../components/DateRangeField'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import PageHeader from '../../components/PageHeader'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import useListParams from '../../components/useListParams'
import { IssuedCertificatesActions, IssuedCertificatesTable } from './IssuedCertificates'
import useIssuedCertificates from './useIssuedCertificates'
import { getPrintPage } from './printService'
import { strings as S } from './strings'
import { serverDetail } from '../../lib/i18n'

/**
 * What this screen keeps in its own address — the programme and the two dates.
 *
 * They were plain state, which made the one screen you would send to a colleague
 * unaddressable: `/print-certificate` always opened on "choose a programme", and the
 * choice was lost again on every Back. `?program=7&from=2026-01-01` IS the certificates
 * that programme issued since January. Defaults stay out of the URL.
 */
const LIST_PARAMS = { program: '', from: '', to: '' }

/**
 * طباعة الشهادة — the legacy `/PrintCertificate`.
 *
 * Built rather than wired (R5): the route rendered a placeholder, so the nav entry and the
 * legacy URL both led to a screen that did nothing. The nav entry stays HIDDEN, as it is in
 * the legacy layout — this is reached by its URL and by the sidebar only when unhidden. The
 * programme report carries the same roster as a panel (`ProgramCertificatesCard`).
 */
export default function PrintCertificatePage() {
  const toast = useToast()

  const [programs, setPrograms] = useState([])
  /* Which certificates are being looked at — in the address, so the view survives a
     refresh and can be sent to someone. See components/useListParams.js. */
  const [params, setParams] = useListParams(LIST_PARAMS)
  const programId = params.program
  const fromDate = params.from
  const toDate = params.to
  const setProgramId = useCallback((next) => setParams({ program: next }), [setParams])
  /* The range is one control, so both ends are written together. */
  const setRange = useCallback(({ from, to }) => setParams({ from, to }), [setParams])

  useEffect(() => {
    getPrintPage()
      .then((page) => setPrograms(page.programs))
      .catch((error) => toast.error(serverDetail(error?.message) || S.fail.page))
  }, [toast])

  const program = useMemo(
    () => programs.find((option) => String(option.value) === String(programId)) ?? null,
    [programs, programId],
  )

  /* The ticked rows are the whole job of this screen — a print run is a dozen people
     picked out of a long list — so they survive leaving and coming back. */
  const certificates = useIssuedCertificates({
    programId,
    program,
    fromDate,
    toDate,
    rememberKey: 'print-certificate:selected',
  })

  return (
    <>
      <PageHeader
        title={S.title}
        count={certificates.loading ? undefined : certificates.rows.length}
        actions={<IssuedCertificatesActions certificates={certificates} />}
      />

      <FilterBar>
        <Select
          value={programId}
          onChange={setProgramId}
          options={programs}
          placeholder={S.filter.program}
          aria-label={S.filter.program}
        />
        <DateRangeField
          value={{ from: fromDate, to: toDate }}
          onChange={setRange}
          labels={{ from: S.filter.from, to: S.filter.to }}
        />
      </FilterBar>

      {!programId && (
        <InfoNote icon={Award}>
          <bdi>{S.chooseProgram}</bdi>
        </InfoNote>
      )}

      <IssuedCertificatesTable certificates={certificates} resetKey={`${programId}|${fromDate}|${toDate}`} />
    </>
  )
}
